import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { LockActionValidationError, useReportsUnsavedChanges } from 'src/components/shared/edit-locking';
import {Box, Button, ExpandableSection, Alert, Container, Header} from '@amzn/awsui-components-react';
import {MultiAllocationWorksheetData} from 'src/models/multidimensional-allocation/MultiAllocationWorksheetData';
import ServiceCollection from 'src/services/ServiceCollection';
import 'src/assets/styles/react-table.scss';
import {
    MultidimensionalAllocationContext
} from "src/services/multidimensional-allocation/MultidimensionalAllocationContext";
import {TPELoadingSpinner} from "src/components/shared/TPELoadingSpinner";
import {
    MultidimensionalAllocationDataSourceContext
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesContext";
import MultidimensionalAllocationDataSourceGrid
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourceGrid";
import ArrayUtils from "src/utils/arrayUtils";
import {
    MULTI_ALLOCATION_DATA_SOURCE_ACTIONS,
    multidimensionalAllocationDataSourcesReducer
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesReducer";
import {ACTIONS} from "src/services/multidimensional-allocation/MultidimensionalAllocationReducer";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import {
    initialState
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesState";
import SimpleUseEffect from "src/components/shared/SimpleUseEffect";
import MultidimensionalAllocationDataSourceSplitGrid
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourceSplitGrid";
import MultidimensionalAllocationDataSourceSelectCOA
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourceSelectCOA";
import MultidimensionalAllocationDataSourcesMagicBox
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesMagicBox";
import MultidimensionalAllocationResultsTable
    from "src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationResultsTable";
import {ExecutionStatus} from "src/models/multidimensional-allocation/ExecutionStatus";
import CONSTANTS from "src/utils/constants";
import { isDasInMdaEnabled, isDerivedMdaAllocationEnabled } from 'src/services/FeatureFlagsService';
import { mergePickerSheets, sheetNamesByValue, toDerivedPickerSheets } from 'src/services/multidimensional-allocation/data-sources/ratioSheetOptions';
import { ApprovalType } from 'src/models/multidimensional-allocation/ProcessApprovals';
import { ProcessApprovalModal } from '../shared/ProcessApprovalModal';
import { useApprovalButtonProps } from '../hooks/useApprovalButtonProps';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { WorksheetExecutionGroupDetails } from 'src/models/multidimensional-allocation/WorksheetExecutionGroupDetails';
import {
    GetMultiAllocationWorksheetExecutionStatusResponse
} from 'src/models/multidimensional-allocation/MultiAllocationWorksheetExecution';
import type {MultiAllocationDataSourceRecord} from 'src/models/multidimensional-allocation/MultiAllocationDataSourceRecord';
import {pickLatestPerDatasource} from 'src/components/multidimensional-allocation/hooks/executionTotal';
import {useMDAResultsData} from 'src/components/multidimensional-allocation/hooks/useMDAResultsData';
import ErrorUtils from 'src/utils/ErrorUtils';
import { isBlockedByEditLock } from 'src/components/shared/edit-locking';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';


const MultidimensionalAllocationDataSourceProvider = (props: any) => {
    const {state, dispatch, services, children, multidimensionalAllocationState} = props;
    const providerValue = React.useMemo(() => ({
        multidimensionalAllocationDataSourcesState: state,
        multidimensionalAllocationDataSourceDispatch: dispatch,
        services,
        multidimensionalAllocationState
    }), [state, dispatch, services]);
    return (
        <MultidimensionalAllocationDataSourceContext.Provider value={providerValue}>
            {children}
        </MultidimensionalAllocationDataSourceContext.Provider>
    );
}

interface MultidimensionalAllocationDataSourcesContainerProps {
    containerTitle?: string,
    expanded?: boolean,
    worksheet?: MultiAllocationWorksheetData,
    services: ServiceCollection,
    executionGroupDetails?: WorksheetExecutionGroupDetails,
    executionStatusResponse?: GetMultiAllocationWorksheetExecutionStatusResponse | null,
    executionStatusLoading?: boolean,
    /** Past view: fetches the execution status again, so its result links are new. */
    refreshResultLinks: () => void,
    loading: boolean
}

/**
 * Container component for managing multidimensional allocation data sources
 * Displays a configurable table using TPEReactTable for data source configuration
 */
export default function MultidimensionalAllocationDataSourcesContainer(props: MultidimensionalAllocationDataSourcesContainerProps) {
    const {containerTitle, expanded, executionGroupDetails, executionStatusResponse, executionStatusLoading = false, refreshResultLinks, loading: parentLoading} = props;
    const {
        services,
        multidimensionalAllocationState,
        multidimensionalAllocationDispatch
    } = React.useContext(MultidimensionalAllocationContext)
    // Set up local data sources state
    const [dataSourcesState, dataSourcesDispatch] = useReducerWithLogger(multidimensionalAllocationDataSourcesReducer, initialState);
    const {
        datasetRecords,
        recordBeingEdited,
        customCoaReference = new Map(),
        recordBeingEditedOriginal,
        selectedCustomCoaValues
    } = dataSourcesState;
    const {sevenSegmentLOVMap, dataSourcesNeedRefresh, viewMode, worksheetExecution} = multidimensionalAllocationState;

    const historicalWorksheetVersion = worksheetExecution?.worksheetVersion;
    const isViewingHistoricalData = historicalWorksheetVersion != null;
    const selectedWorksheet = multidimensionalAllocationState.selectedWorksheet;

    // A version switch makes the view re-fetch the worksheet (a NEW object); fetching data sources before that
    // lands would use the pre-switch version. A mount that already sees a past period has no switch to wait for.
    const worksheetAtVersionSwitch = useRef({ version: historicalWorksheetVersion, worksheet: selectedWorksheet, atMount: true });
    if (worksheetAtVersionSwitch.current.version !== historicalWorksheetVersion) {
        worksheetAtVersionSwitch.current = { version: historicalWorksheetVersion, worksheet: selectedWorksheet, atMount: false };
    }
    const historicalWorksheetReady = isViewingHistoricalData
        && !parentLoading
        && (worksheetAtVersionSwitch.current.atMount || selectedWorksheet !== worksheetAtVersionSwitch.current.worksheet);

    // Historical mode fetches at the backend-resolved version (never the raw logical one) once the gate opens.
    const worksheetVersionToUse = isViewingHistoricalData
        ? (historicalWorksheetReady ? selectedWorksheet?.worksheetVersion : undefined)
        : selectedWorksheet?.worksheetVersion;
    const latestFetchKey = useRef<{ worksheetId?: string; worksheetVersion?: number }>({});
    latestFetchKey.current = { worksheetId: selectedWorksheet?.worksheetId, worksheetVersion: worksheetVersionToUse };

    const [loadTime, setLoadTime] = React.useState('');
    const [recordNeedsSaving, setRecordNeedsSaving] = React.useState(undefined as string | undefined);
    const [pullDataSourceBalance, setPullDataSourceBalance] = React.useState(undefined as string | undefined);

    // SelectCOA populates this on render; null during first-render race.
    const selectCoaSaveRef = useRef<(() => boolean) | null>(null);
    const saveCompletionCallbacks = useRef<{ resolve: () => void; reject: (e: unknown) => void } | null>(null);

    useReportsUnsavedChanges(useCallback(() => new Promise<void>((resolve, reject) => {
        if (!recordBeingEdited?.isEditing) { resolve(); return; }
        if (!selectCoaSaveRef.current) { resolve(); return; }
        if (!selectCoaSaveRef.current()) {
            reject(new LockActionValidationError('Please fix the errors on the data source being edited before saving.'));
            return;
        }
        saveCompletionCallbacks.current = { resolve, reject };
    }), [recordBeingEdited?.isEditing]));

    const [tableKey, setTableKey] = React.useState(0);
    const [refreshRecordsFlag, setRefreshRecordsFlag] = useState(undefined as string | undefined);
    const [refreshBalanceStatusFlag, setRefreshBalanceStatusFlag] = useState(undefined as string | undefined);
    const [currentRefreshExecutionId, setCurrentRefreshExecutionId] = useState(undefined as string | undefined);
    const {showDataKeySelection = false} = recordBeingEdited || {};
    const [atLeastOneDatasetItemIsSaved, setAtLeastOneDatasetItemIsSaved] = useState(false);
    const [loadingBalanceStatuses, setLoadingBalanceStatuses] = useState(false);
    const [modalMode, setModalMode] = useState<'closed' | 'submit' | 'approval'>('closed');

    const [worksheetDataSourcesResult, customCoaReferenceMap, worksheetDataSourcesLoading, worksheetDataSourcesError] =
        services.multidimensionalAllocationService.getMultiAllocationWorksheetDataSources(
            multidimensionalAllocationState.selectedWorksheet?.worksheetId,
            worksheetVersionToUse,
            refreshRecordsFlag || loadTime
        )

    const statusByDatasource = useMemo(
        () => pickLatestPerDatasource(executionStatusResponse?.datasourceStatuses),
        [executionStatusResponse]);

    // A result link expires five minutes after the status call, so an opened past-view data source shows its link
    // only after a status newer than the open or the period switch.
    const openedDatasourceId = isViewingHistoricalData ? recordBeingEdited?.datasourceId : undefined;
    const executionId = worksheetExecution?.executionId;
    const openKey = `${executionId}|${openedDatasourceId}`;
    const statusAtOpen = useRef({ openKey, response: executionStatusResponse, fetchSeen: false });
    if (statusAtOpen.current.openKey !== openKey) {
        statusAtOpen.current = { openKey, response: executionStatusResponse, fetchSeen: false };
    }
    if (executionStatusLoading) {
        statusAtOpen.current.fetchSeen = true;
    }
    // A failed fetch can leave the same response in place, so a fetch seen to finish also counts as new.
    const newStatusArrived = executionStatusResponse !== statusAtOpen.current.response
        || (statusAtOpen.current.fetchSeen && !executionStatusLoading);
    const linkPendingFor = newStatusArrived ? undefined : openedDatasourceId;

    // A period switch fetches the status by itself, so only an open within the same execution asks for new links.
    // A switch also reloads the data sources, which closes a viewed one; closing it first skips a wasted download.
    const viewedDatasourceId = recordBeingEdited?.isEditing ? undefined : recordBeingEdited?.datasourceId;
    const previousSelection = useRef({ executionId, viewedDatasourceId });
    const periodJustChanged = previousSelection.current.executionId !== executionId;
    useEffect(() => {
        const previous = previousSelection.current;
        previousSelection.current = { executionId, viewedDatasourceId };
        if (previous.executionId !== executionId) {
            if (previous.viewedDatasourceId) {
                dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CANCEL_EDITING);
            }
        } else if (openedDatasourceId) {
            refreshResultLinks();
        }
    }, [openedDatasourceId, viewedDatasourceId, executionId, refreshResultLinks, dataSourcesDispatch]);

    // Historical mode shows only what the selected execution produced; the definition row's s3PresignUrl /
    // totalBalance (last manual run) are never a fallback. The current view keeps the raw reducer state.
    const displayedState = useMemo(() => {
        if (!isViewingHistoricalData) {
            return dataSourcesState;
        }
        const overlayExecutionStatus = (record: MultiAllocationDataSourceRecord): MultiAllocationDataSourceRecord => {
            const status = statusByDatasource.get(record.datasourceId);
            return {
                ...record,
                s3PresignUrl: record.datasourceId === linkPendingFor ? undefined : status?.s3PresignUrl,
                totalBalance: undefined,
                balancePullStatus: status?.executionStatus,
                lastExecutedTime: status?.executionTime,
            };
        };
        const overlaidRecords = dataSourcesState.datasetRecords.map(overlayExecutionStatus);
        const overlaidRecordBeingEdited = dataSourcesState.recordBeingEdited
            ? overlaidRecords.find(r => r.datasourceId === dataSourcesState.recordBeingEdited!.datasourceId)
            : undefined;
        return { ...dataSourcesState, datasetRecords: overlaidRecords, recordBeingEdited: overlaidRecordBeingEdited };
    }, [dataSourcesState, isViewingHistoricalData, statusByDatasource, linkPendingFor]);
    const displayedRecordBeingEdited = displayedState.recordBeingEdited;
    const resultLinkPending = linkPendingFor != null && linkPendingFor === displayedRecordBeingEdited?.datasourceId;
    // One download per opened data source: the results table and the past-view total read this same file.
    // A period change closes the open data source, so the render that sees the change downloads nothing.
    const resultFile = useMDAResultsData(showDataKeySelection && !periodJustChanged ? displayedRecordBeingEdited?.s3PresignUrl : undefined);

    // Past view: the shown link may have expired since the data source was opened, so Download asks for a new one.
    const displayedDatasourceId = displayedRecordBeingEdited?.datasourceId;
    const getPastDownloadUrl = useCallback(async () => {
        if (!selectedWorksheet?.worksheetId || !executionId || !displayedDatasourceId) {
            return undefined;
        }
        try {
            const response = await services.multidimensionalAllocationService
                .fetchMultiAllocationExecutionStatus(selectedWorksheet.worksheetId, executionId);
            return pickLatestPerDatasource(response?.datasourceStatuses).get(displayedDatasourceId)?.s3PresignUrl;
        } catch (ex) {
            services.messageService.showErrorBanner(`Failed to get a new download link: ${ErrorUtils.getMessage(ex)}`);
            return undefined;
        }
    }, [services, selectedWorksheet?.worksheetId, executionId, displayedDatasourceId]);

    const [dataSourceTypes, currencyCodes, periods, currencyConversionTypes, loadingCheck, error] =
        services.dataSourcesService.getDataSourceLOVs(loadTime);

    const [coaSegmentsResponse, coaSegmentsLoading, coaSegmentError] =
        services.tpAllocationService.getAllCoaSegments(multidimensionalAllocationState.selectedWorksheet?.worksheetId);

    const [coaTypes, loadingCoa, errorCoa] = services.dataSourcesService.getCoaTypes(loadTime);

    const [allocationSheetsResponse, allocationSheetsLoading, allocationSheetsError] =
        services.multidimensionalAllocationService.listMultiAllocationSheets(loadTime, CONSTANTS.STATUS.ACTIVE);

    // Derived Allocation Sheets are offered beside uploaded sheets only while DAS in MDA is on. Ask for ACTIVE: the
    // WebAPI then returns the live row of a sheet that also has an open draft, so the picker keeps that sheet.
    const derivedSheetsEnabled = isDasInMdaEnabled() && isDerivedMdaAllocationEnabled();
    const [derivedSheetsResponse, derivedSheetsLoading, derivedSheetsError] =
        services.derivedAllocationSheetService.listDerivedAllocationSheets(
            derivedSheetsEnabled ? loadTime : '', CONSTANTS.STATUS.ACTIVE);

    const [saveDataSourceResponse, saveDataSourceLoading, saveDataSourceError] =
        services.multidimensionalAllocationService.saveMultiAllocationWorksheetDataSource(false, recordBeingEdited, customCoaReferenceMap ?? undefined, recordNeedsSaving);

    const [datasourceExecutionId, executeMultiAllocationWorksheetDataSourceLoading, executeMultiAllocationWorksheetDataSourceError] =
        services.multidimensionalAllocationService.executeMultiAllocationWorksheetDataSource(recordBeingEdited?.worksheetId,
            recordBeingEdited?.worksheetVersion, recordBeingEdited?.datasourceId, pullDataSourceBalance)

    const [datasourceExecutionStatusResponse, datasourceExecutionStatusLoading, datasourceExecutionStatusError] = 
        services.multidimensionalAllocationService.getMultiAllocationExecutionStatus(
            multidimensionalAllocationState.selectedWorksheet?.worksheetId,
            currentRefreshExecutionId,
            refreshBalanceStatusFlag
        );

    useEffect(() => {
        setLoadTime(new Date().toDateString());
        setTableKey(tableKey + 1);
    }, []);

    useEffect(() => {
        setAtLeastOneDatasetItemIsSaved(datasetRecords.some(x => !x.isNewRecord))
    }, [datasetRecords])

    useEffect(() => {
        if (saveDataSourceError) {
            services.messageService.showErrorBanner(saveDataSourceError);
            // Clear the trigger on a refused save too, or the save hook re-sends the next row opened.
            setRecordNeedsSaving(undefined);
            saveCompletionCallbacks.current?.reject(new Error(saveDataSourceError));
            saveCompletionCallbacks.current = null;
            return;
        }

        if (saveDataSourceResponse == null) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SAVE_RECORD.withPayload({
            recordID: recordBeingEdited?.datasourceId,
            updatedRecord: saveDataSourceResponse.dataSource
        }));

        // Clear the recordNeedsSaving flag after processing the save response
        setRecordNeedsSaving(undefined);
        saveCompletionCallbacks.current?.resolve();
        saveCompletionCallbacks.current = null;
    }, [saveDataSourceResponse, saveDataSourceError])

    useEffect(() => {
        if (dataSourcesNeedRefresh) {
            setRefreshRecordsFlag(new Date().toISOString());
        }
    }, [dataSourcesNeedRefresh])

    // Handle pull balance execution response - update lastBalancePullId locally
    useEffect(() => {
        if (executeMultiAllocationWorksheetDataSourceError) {
            services.messageService.showErrorBanner(executeMultiAllocationWorksheetDataSourceError);
            // Clear the trigger on a refused pull too, or the next row opened re-sends Execute.
            setPullDataSourceBalance(undefined);
            return;
        }

        if (datasourceExecutionId && recordBeingEdited?.datasourceId) {
            dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_LAST_BALANCE_PULL_ID.withPayload({
                datasourceId: recordBeingEdited.datasourceId,
                lastBalancePullId: datasourceExecutionId
            }));
            
            setCurrentRefreshExecutionId(datasourceExecutionId);
            
            // Reset pullDataSourceBalance to prevent re-triggering execute API
            setPullDataSourceBalance(undefined);
        }
    }, [datasourceExecutionId, executeMultiAllocationWorksheetDataSourceError]);

    // Handle data source execution status response - refresh when completed
    useEffect(() => {
        if (datasourceExecutionStatusError) {
            services.messageService.showErrorBanner(datasourceExecutionStatusError);
            return;
        }

        if (datasourceExecutionStatusResponse?.datasourceStatuses &&
            datasourceExecutionStatusResponse.datasourceStatuses.length > 0 &&
            refreshBalanceStatusFlag) {
          
            // Datasource execution status response will only have one datasource 
            // TODO: Backend API to return execution status in response directly from backend
            const firstStatus = datasourceExecutionStatusResponse.datasourceStatuses[0];
            if (firstStatus) {
                if (firstStatus.executionError) {
                    services.messageService.showErrorBanner(firstStatus.executionError);
                    return;
                }

                const statusMap = new Map<string, string>();
                statusMap.set(firstStatus.datasourceId, firstStatus.executionStatus);
                dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_BALANCE_PULL_STATUS.withPayload(statusMap));
            }
            
            // Refresh data sources when the first status is completed
            if (firstStatus?.executionStatus === ExecutionStatus.COMPLETED) {
                multidimensionalAllocationDispatch(ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.withPayload(true));
            }
        }
    }, [datasourceExecutionStatusResponse, datasourceExecutionStatusError, refreshBalanceStatusFlag, multidimensionalAllocationDispatch]);

    // Handle refresh balance status action
    useEffect(() => {
        if (dataSourcesState.refreshBalanceStatusDatasourceId) {
            const targetRecord = datasetRecords.find(r => r.datasourceId === dataSourcesState.refreshBalanceStatusDatasourceId);
            if (targetRecord?.lastBalancePullId) {
                setCurrentRefreshExecutionId(targetRecord.lastBalancePullId);
                setRefreshBalanceStatusFlag(new Date().toISOString());
            }
            // Always clear the refresh trigger regardless of whether we have lastBalancePullId
            dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REFRESH_BALANCE_STATUS.withPayload(undefined));
        }
    }, [dataSourcesState.refreshBalanceStatusDatasourceId]);

    useEffect(() => {
        if (worksheetDataSourcesError) {
            services.messageService.showErrorBanner(worksheetDataSourcesError);
            return;
        }

        if (worksheetDataSourcesResult == null || worksheetDataSourcesLoading) {
            return;
        }

        if (isViewingHistoricalData) {
            // Records are stamped with the requested worksheetId/version (transformDataSource); a result
            // stamped for another key is an older in-flight response and must not overwrite the table.
            const first = worksheetDataSourcesResult[0];
            if (first && (first.worksheetId !== latestFetchKey.current.worksheetId
                || first.worksheetVersion !== latestFetchKey.current.worksheetVersion)) {
                return;
            }
            if (worksheetDataSourcesResult.length === 0) {
                dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_RECORDS.withPayload([]));
                return;
            }
        } else if (worksheetDataSourcesResult.length == 0 && !worksheetDataSourcesLoading) {
            // Only seed a blank row when we haven't already — otherwise every
            // re-fetch (loadTime / refreshRecordsFlag change) would append another
            // empty row on top of the ones the reducer is already tracking.
            if (datasetRecords.length === 0 && viewMode === CONSTANTS.VIEW_MODE.EDITABLE) {
                addNewRecord();
            }
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_RECORDS.withPayload(worksheetDataSourcesResult))

        const fetchStatuses = async () => {
            if (worksheetDataSourcesResult.length > 0 && executionStatusResponse) {
                setLoadingBalanceStatuses(true);
                
                try {
                    const statusMap = new Map<string, string>();
                    // Use datasourceStatuses from the execution response
                    if (executionStatusResponse.datasourceStatuses && executionStatusResponse.datasourceStatuses.length > 0) {
                        executionStatusResponse.datasourceStatuses.forEach((dsStatus: any) => {
                            statusMap.set(dsStatus.datasourceId, dsStatus.executionStatus || "Not Started");
                        });
                    } else {
                        // Fallback to overall execution status if no datasource-specific statuses
                        worksheetDataSourcesResult.forEach(record => {
                            statusMap.set(record.datasourceId, executionStatusResponse?.executionStatus || "Not Started");
                        });
                    }

                    if (statusMap.size > 0) {
                        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_BALANCE_PULL_STATUS.withPayload(statusMap));
                    }
                } catch (error) {
                    console.error("Failed to fetch data source status:", error);
                }
                
                setLoadingBalanceStatuses(false);
            }
        };

        fetchStatuses();
        
    }, [worksheetDataSourcesResult, worksheetDataSourcesLoading, worksheetDataSourcesError]);

    useEffect(() => {
        if (datasetRecords == null) {
            return;
        }

        multidimensionalAllocationDispatch(ACTIONS.SET_DATASET_RECORDS.withPayload(datasetRecords))
    }, [datasetRecords]);

    useEffect(() => {
        if (customCoaReferenceMap == null) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_CUSTOM_COA_REFERENCE.withPayload(customCoaReferenceMap))
    }, [customCoaReferenceMap, dataSourcesDispatch]);

    useEffect(() => {
        if (error) {
            services.messageService.showErrorBanner(error);
            return;
        }

        if (ArrayUtils.isNullOrEmpty(dataSourceTypes) || ArrayUtils.isNullOrEmpty(currencyCodes)
            || ArrayUtils.isNullOrEmpty(periods) || ArrayUtils.isNullOrEmpty(currencyConversionTypes)) {
            return;
        }

        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATASOURCE_LOV.withPayload({
            dataSourceTypes, currencyCodes, periods, currencyConversionTypes
        }))
    }, [dataSourceTypes, error, currencyCodes, periods, currencyConversionTypes, dataSourcesDispatch]);

    useEffect(() => {
        if (coaSegmentError) {
            services.messageService.showErrorBanner(coaSegmentError);
            return;
        }

        if (coaSegmentsResponse == null) {
            return;
        }

        multidimensionalAllocationDispatch(ACTIONS.SET_SEVEN_SEGMENTS_LOV_MAP.withPayload(coaSegmentsResponse))
    }, [coaSegmentsResponse, coaSegmentError, multidimensionalAllocationDispatch]);

    useEffect(() => {
        if (allocationSheetsLoading || derivedSheetsLoading) {
            dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ALLOCATION_SHEETS_LOADING.withPayload(true));
        }
    }, [allocationSheetsLoading, derivedSheetsLoading, dataSourcesDispatch]);

    useEffect(() => {
        if (allocationSheetsError) {
            services.messageService.showErrorBanner(allocationSheetsError);
            return;
        }

        if (allocationSheetsResponse == null) {
            return;
        }
        // Wait for the derived list too, so the picker is filled once, and does not show as loaded while it is not.
        if (derivedSheetsEnabled && derivedSheetsLoading) {
            return;
        }

        // A failed derived-sheet list still leaves the uploaded sheets usable.
        if (derivedSheetsEnabled && derivedSheetsError) {
            services.messageService.showErrorBanner(derivedSheetsError);
        }
        const derivedSheets = derivedSheetsEnabled ? toDerivedPickerSheets(derivedSheetsResponse?.sheets) : [];
        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ALLOCATION_SHEETS.withPayload(
            mergePickerSheets(allocationSheetsResponse.sheets, derivedSheets)));
    }, [allocationSheetsResponse, allocationSheetsError, derivedSheetsEnabled, derivedSheetsResponse,
        derivedSheetsError, derivedSheetsLoading, dataSourcesDispatch]);

    // Every sheet's name, including derived sheets the picker leaves out (no segment), to name a saved one.
    const sheetNames = React.useMemo(() => sheetNamesByValue(allocationSheetsResponse?.sheets,
        derivedSheetsEnabled ? derivedSheetsResponse?.sheets : undefined),
        [allocationSheetsResponse, derivedSheetsEnabled, derivedSheetsResponse]);

    const addNewRecord = function () {
        dataSourcesDispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.ADD_NEW_RECORD
            .withPayload({
                worksheetId: multidimensionalAllocationState.selectedWorksheet?.worksheetId,
                worksheetVersion: multidimensionalAllocationState.selectedWorksheet?.worksheetVersion,
                worksheetName: multidimensionalAllocationState.selectedWorksheet?.worksheetName
            }));
    }

    // Handle success callback for approval modal
    const handleApprovalSuccess = () => {
        // Refresh the data or navigate as needed
        window.location.reload();
    };

    // Compute validation message for the Submit for Review button
    const getValidationMessage = (): string | undefined => {
        // Check if there are any errors in data sources
        const hasDataSourceErrors = datasetRecords.some(record => 
            record.errors != null && record.errors.size > 0
        );
        if (hasDataSourceErrors) {
            return 'Cannot submit worksheet with data source errors';
        }

        // Check if there are no saved data sources
        const hasSavedDataSources = datasetRecords.some(record => !record.isNewRecord);
        if (!hasSavedDataSources) {
            return 'Must add at least one data source before submitting';
        }

        // Check if worksheet is not executed (balance not pulled for all saved data sources)
        const savedDataSources = datasetRecords.filter(record => !record.isNewRecord);
        const allDataSourcesExecuted = savedDataSources.every(record => 
            record.lastBalancePullId != null && record.lastBalancePullId !== ''
        );
        if (!allDataSourcesExecuted) {
            return 'Must execute worksheet (pull balance) for all data sources before submitting';
        }

        // Check if group is not assigned to the worksheet
        if (executionGroupDetails?.executionGroup == null) {
            return 'Must assign worksheet to an execution group before submitting';
        }

        return undefined;
    };

    // Get button properties using the reusable hook
    const approvalButtonProps = useApprovalButtonProps(
        (multidimensionalAllocationState.selectedWorksheet?.status as WorksheetStatus) || 'DRAFT_IN_PROGRESS',
        () => setModalMode('submit'),
        () => setModalMode('approval'),
        undefined, // Worksheets don't have validation data like sheets do
        getValidationMessage
    );

    const loading = worksheetDataSourcesLoading || loadingBalanceStatuses || parentLoading;
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);

    // The footer button is polymorphic: "Submit for Review" in draft, "Approval Decision" once
    // pending approval. The lock rule applies only to submitting — passing isLockableAction=false
    // for any other status keeps a reviewer's approval enabled (they never hold the lock, and the
    // lock bar is not rendered in that state, so the reason would be unclearable).
    // Under read-only mode (canEdit=false), the edit-lock gate is bypassed for Submit-for-Review so
    // accounting can still submit a Draft-In-Progress sheet without acquiring the lock (which
    // would unblock the whole edit surface). Add-data-source below is still viewMode-gated.
    const isSubmitAction = (multidimensionalAllocationState.selectedWorksheet?.status as WorksheetStatus
        || 'DRAFT_IN_PROGRESS') === 'DRAFT_IN_PROGRESS';
    const submitBlockedByLock = canEdit && isBlockedByEditLock(viewMode, viewMode != null, isSubmitAction);

    return (
        <Container 
                data-class="polarisExpandableSection"
                header={<Header variant='h3'> { containerTitle } </Header>}
                footer={ <div className="actionButtonsContainer">
                            <Box float="left">
                                <Button
                                    variant={"normal"}
                                    onClick={addNewRecord}
                                    // !canEdit fallback: viewMode is only set when the edit-lock flag is on. With the flag
                                    // off, viewMode stays undefined and the edit-lock disable term is false, so read-only
                                    // users would otherwise be able to add data sources. Gate on canEdit as well.
                                    disabled={loading || recordBeingEdited?.isEditing || isViewingHistoricalData || !canEdit || (viewMode != null && viewMode !== CONSTANTS.VIEW_MODE.EDITABLE)}
                                    disabledReason={
                                        isViewingHistoricalData
                                            ? "Cannot add data sources when viewing historical data"
                                            : (!canEdit ? "Read-only mode is active." : (!loading && viewMode != null && viewMode !== CONSTANTS.VIEW_MODE.EDITABLE ? "Click Edit to make changes." : undefined))
                                    }
                                >
                                    Add data source
                                </Button>
                            </Box>
                            
                            {multidimensionalAllocationState.selectedWorksheet?.worksheetId && (
                                <Box float="right">
                                    <Button
                                        variant="primary"
                                        onClick={approvalButtonProps.onClick}
                                        disabled={loading || approvalButtonProps.disabled || recordBeingEdited?.isEditing || isViewingHistoricalData || submitBlockedByLock}
                                        disabledReason={
                                            isViewingHistoricalData
                                                ? "Cannot submit historical data for approval"
                                                : (submitBlockedByLock
                                                    ? "Click Edit to acquire the lock before submitting."
                                                    : approvalButtonProps.disabledReason)
                                        }
                                    >
                                        {approvalButtonProps.label}
                                    </Button>
                                </Box>
                            )}
                        </div>
                    }
        >
           
            <MultidimensionalAllocationDataSourceProvider
                services={services}
                state={displayedState}
                dispatch={dataSourcesDispatch}
                multidimensionalAllocationState={multidimensionalAllocationState}
            >
                <div className="wizardBoxContentContainer">
                    {isViewingHistoricalData && (
                        <Alert
                            type="info"
                            header="Viewing Historical Data"
                            dismissible={false}
                        >
                            You are viewing execution data from {multidimensionalAllocationState.selectedExecutionPeriod}.
                            Data is read-only in this view. Clear the period selection to return to current data.
                        </Alert>
                    )}
                    <div className="datasourceHeader">
                        <TPELoadingSpinner loading={loading} loadingText={"Loading data sources"}>
                            <MultidimensionalAllocationDataSourceGrid key={1}/>
                        </TPELoadingSpinner>
                    </div>
                    {showDataKeySelection &&
                        <div className="dataKeyContainer">
                            <MultidimensionalAllocationDataSourceSelectCOA {...{
                                allRecordDescriptions: datasetRecords.map(x => x.description),
                                coaTypes,
                                customCoaReference,
                                dispatch: dataSourcesDispatch,
                                isSaving: saveDataSourceLoading,
                                onRecordNeedsSaving: (x: string) => setRecordNeedsSaving(x),
                                originalRecordDescription: recordBeingEditedOriginal?.description || '',
                                recordBeingEdited: {
                                    ...recordBeingEdited,
                                    entityId: recordBeingEdited?.datasourceId || ''
                                },
                                services,
                                sevenSegmentLOV: sevenSegmentLOVMap,
                                showDrillDownSelect: false,
                                saveButtonLabel: "Save data source",
                                saveRecordRef: selectCoaSaveRef,
                            }}
                            />
                            <MultidimensionalAllocationDataSourcesMagicBox
                                key={recordBeingEdited?.datasourceId}
                                recordBeingEdited={displayedRecordBeingEdited}
                                services={services}
                                dispatch={dataSourcesDispatch}
                                ACTIONS={MULTI_ALLOCATION_DATA_SOURCE_ACTIONS}
                                coaTypes={coaTypes}
                                selectedCustomCoaValues={selectedCustomCoaValues}
                                sevenSegmentLOV={sevenSegmentLOVMap}
                                setPullDataSourceBalance={setPullDataSourceBalance}
                                filteredAllocationSheets={dataSourcesState.filteredAllocationSheets}
                                allocationSheets={dataSourcesState.allocationSheets}
                                sheetNames={sheetNames}
                                allocationSheetsLoading={dataSourcesState.allocationSheetsLoading}
                                pullBalanceLoading={executeMultiAllocationWorksheetDataSourceLoading}
                                isViewingHistoricalData={isViewingHistoricalData}
                                resultLinkPending={resultLinkPending}
                                resultFile={resultFile}
                            />
                        </div>
                    }
                    {(showDataKeySelection && displayedRecordBeingEdited?.s3PresignUrl) &&
                        <div>
                            <MultidimensionalAllocationResultsTable
                                preSignedUrl={displayedRecordBeingEdited?.s3PresignUrl}
                                resultFile={resultFile}
                                getDownloadUrl={isViewingHistoricalData ? getPastDownloadUrl : undefined}
                            />
                        </div>
                    }
                    {recordBeingEdited != undefined && datasetRecords.indexOf(recordBeingEdited) != datasetRecords.length - 1 &&
                        <div>
                            <MultidimensionalAllocationDataSourceSplitGrid key={tableKey}/>
                        </div>
                    }
                    <SimpleUseEffect
                        useEffectVars={[recordNeedsSaving]}
                        action={() => {
                            // Only trigger refresh after a successful save, not on toggle actions
                            if (saveDataSourceResponse && recordNeedsSaving) {
                                multidimensionalAllocationDispatch(ACTIONS.CHECK_AND_REFRESH_ENTITY_DETAILS);
                            }
                        }}
                    />
                    
                    {/* Process Approval Modal */}
                    {multidimensionalAllocationState.selectedWorksheet?.worksheetId && (
                        <ProcessApprovalModal
                            entityId={multidimensionalAllocationState.selectedWorksheet.worksheetId}
                            approvalType={ApprovalType.MULTI_ALLOCATION_WORKSHEET}
                            validationData={undefined} // Worksheets don't have validation data like sheets do
                            mode={modalMode}
                            onModeChange={setModalMode}
                            onSuccess={handleApprovalSuccess}
                        />
                    )}
                </div>
            </MultidimensionalAllocationDataSourceProvider>
        </Container>
    )
}
